import React, { useState, useMemo } from 'react';
import useGameStore from '../../store/gameStore';
import useAudioStore from '../../store/audioStore';
import { formatMoney } from '../../utils/format';
import { computeContractMargin } from '../../utils/contractMargin';
import { getDriverSpecialties } from '../../utils/driverSpecialties';
import { checkAssignmentHseBlock } from '../../store/helpers/hseCompliance';
import { MARKET_EQUILIBRIUM_MAX_GROSS_MARGIN } from '../../store/helpers/marketEquilibrium';
import { Gauge } from './Gauge';
import { Truck, User, X, Check, AlertCircle, AlertTriangle, Fuel, Shield, Zap, Lock, BadgeCheck, TrendingUp, TrendingDown } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { VAT_REGIMES, getVatRegime } from '../../data/internationalTrade';
import MarkdownLite from '../MarkdownLite';
import { evaluateContractCoverage, depotCoversContract, getVehicleTransferCost } from '../../data/depotGeo';
import { findCityById } from '../../data/mapCities';
import { EXPORT_COUNTRIES } from '../../data/localExport';
import { toast } from '../ui/sonner';

/**
 * 13/06/2026 — Chantier Dépôts Phase 1. Bannière affichée quand le contrat
 * démarre hors du rayon d'action de tous les dépôts : pénalité de revenus
 * variable ≥ 40 % appliquée à l'acceptation.
 */
function OutOfZoneBanner({ contract }) {
  const { t } = useTranslation();
  const ownedDepots = useGameStore(s => s.ownedDepots);
  const company = useGameStore(s => s.company);
  const activeInvestments = useGameStore(s => s.activeInvestments);
  const coverage = useMemo(
    () => evaluateContractCoverage(contract, ownedDepots || [], company, activeInvestments),
    [contract, ownedDepots, company, activeInvestments],
  );
  if (coverage.covered) return null;
  const originCity = findCityById(contract.originCityId);
  const nearestCity = findCityById(coverage.nearestDepotCityId);
  return (
    <div
      className="mx-5 mt-3 bg-amber-500/10 border border-amber-500/40 rounded-lg px-3 py-2.5 flex gap-2 items-start"
      data-testid="out-of-zone-warning"
    >
      <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
      <p className="text-xs text-amber-100 leading-snug">
        <MarkdownLite as="span" text={t('assign_out_of_zone_banner', {
          city: originCity?.name || '?',
          distance: coverage.distanceKm,
          depotCity: nearestCity?.name || '?',
          pct: Math.round(coverage.penalty * 100),
        })} />
      </p>
    </div>
  );
}
import AssignmentConflictModal from './AssignmentConflictModal';
import InternationalTradePanel from './InternationalTradePanel';
import {
  CATEGORY_VEHICLE_REQUIREMENTS,
  vehicleSatisfiesContract,
  driverSatisfiesContract,
  driverCanDriveVehicle,
  hasSpecialtyMatch,
} from '../../utils/contractCompatibility';

/**
 * ContractAssignModal — vehicle/driver picker for accepting a contract.
 *
 * 04/06/2026 refactor : compatibility resolvers moved to
 *   `/utils/contractCompatibility.js`, the assignment-conflict sub-modal to
 *   `./AssignmentConflictModal.jsx`, the Incoterm/VAT panel to
 *   `./InternationalTradePanel.jsx`. Zero behaviour change — same data-testid
 *   surface, same flow.
 */
export const ContractAssignModal = ({ contract, onClose }) => {
  const { t } = useTranslation();
  const { vehicles, drivers, acceptContract, ownedDepots, company } = useGameStore();
  const activeInvestments = useGameStore(s => s.activeInvestments) || {};
  const playSound = useAudioStore((s) => s.play);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState(null);

  // 13/06/2026 — Chantier Dépôts : couverture géographique du contrat.
  const geoCoverage = useMemo(
    () => evaluateContractCoverage(contract, ownedDepots || [], company, activeInvestments),
    [contract, ownedDepots, company, activeInvestments],
  );

  // 13/06/2026 — Cohérence stricte (retour utilisateur : « on me propose
  // encore des chauffeurs ou camions déjà sur d'autres missions »).
  // Un véhicule n'est proposable QUE s'il est réellement libre : statut
  // 'idle' (ou absent) ET aucun contrat assigné. Un camion en panne /
  // maintenance pendant un contrat porte toujours son assignedContractId
  // → il est désormais exclu au lieu de réapparaître dans la liste.
  // Phase 3 : si le contrat est couvert, seuls les camions d'un dépôt qui
  // couvre la ville d'origine sont proposés (transfert sinon).
  // 05/02/2026 (Q1 Lot 1) — Retour user : on liste maintenant les véhicules
  // libres mais affectés à un MAUVAIS dépôt (`wrongDepotVehicles`) pour
  // pouvoir les transférer vers un dépôt couvrant le contrat avant
  // assignation (panneau `WrongDepotTransferPanel` plus bas).
  const { idleVehicles, wrongDepotVehicles, targetDepotForTransfer } = useMemo(() => {
    const free = vehicles.filter(v =>
      (v.status === 'idle' || v.status == null) && !v.assignedContractId
    );
    if (!geoCoverage.covered || !contract.originCityId) {
      return { idleVehicles: free, wrongDepotVehicles: [], targetDepotForTransfer: null };
    }
    const eligible = [];
    const wrong = [];
    free.forEach(v => {
      const vDepot = (ownedDepots || []).find(d => d.id === (v.depotId || 'depot-hq'));
      if (!vDepot) { eligible.push(v); return; }
      if (depotCoversContract(vDepot, contract, company, activeInvestments)) {
        eligible.push(v);
      } else {
        wrong.push({ vehicle: v, depot: vDepot });
      }
    });
    // Pick the FIRST depot that covers the contract as the transfer target
    // (most often the QG / a satellite covering the origin city).
    const target = (ownedDepots || []).find(d => depotCoversContract(d, contract, company, activeInvestments)) || null;
    return { idleVehicles: eligible, wrongDepotVehicles: wrong, targetDepotForTransfer: target };
  }, [vehicles, geoCoverage, contract, ownedDepots, company, activeInvestments]);
  const wrongDepotCount = wrongDepotVehicles.length;

  // Même règle pour les chauffeurs : exclus s'ils sont en formation,
  // malades, en congés, en grève, en retour de mission ponctuelle (06/02/2026)
  // OU rattachés à un véhicule qui sert un contrat (même si ce véhicule est
  // temporairement en panne).
  const availableDrivers = useMemo(
    () => drivers.filter(d => {
      if (d.status === 'training' || d.status === 'sick' || d.status === 'vacation'
        || d.status === 'holiday' || d.status === 'striking' || d.status === 'returning') return false;
      if (d.onHoliday) return false;
      if (d.assignedVehicleId) {
        const v = vehicles.find(vv => vv.id === d.assignedVehicleId);
        if (v && (v.status === 'active' || v.assignedContractId)) return false;
      }
      return true;
    }),
    [drivers, vehicles],
  );

  // Vehicle / driver lists with compatibility metadata, sorted ok-first.
  const vehiclesWithCompat = useMemo(() => {
    return idleVehicles
      .map(v => ({ v, compat: vehicleSatisfiesContract(v, contract) }))
      .sort((a, b) => Number(b.compat.ok) - Number(a.compat.ok));
  }, [idleVehicles, contract]);

  const driversWithCompat = useMemo(() => {
    return availableDrivers
      .map(d => ({
        d,
        compat: driverSatisfiesContract(d, contract),
        bonus: hasSpecialtyMatch(d, contract),
      }))
      .sort((a, b) => {
        if (a.compat.ok !== b.compat.ok) return Number(b.compat.ok) - Number(a.compat.ok);
        return Number(b.bonus) - Number(a.bonus);
      });
  }, [availableDrivers, contract]);

  const selectedVehicle = vehicles.find(v => v.id === selectedVehicleId);
  const selectedDriver = drivers.find(d => d.id === selectedDriverId);
  const vehicleCompat = selectedVehicle ? vehicleSatisfiesContract(selectedVehicle, contract) : null;
  const driverCompat = selectedDriver ? driverSatisfiesContract(selectedDriver, contract) : null;
  const permitCompat = driverCanDriveVehicle(selectedDriver, selectedVehicle);
  const totalDays = useGameStore(s => s.totalDays) || 0;
  const hseBlock = (selectedDriver && selectedVehicle)
    ? checkAssignmentHseBlock(selectedDriver, selectedVehicle, totalDays)
    : { blocked: false };
  const canConfirm = selectedVehicle && selectedDriver && vehicleCompat?.ok && driverCompat?.ok && permitCompat.ok && !hseBlock.blocked;
  const specialtyBonus = selectedDriver && hasSpecialtyMatch(selectedDriver, contract);

  // Margin (precise) — visible when TMS + commercial unlock is on AND both
  // vehicle + driver are selected.
  const hiredSupport = useGameStore(s => s.hiredSupport) || {};
  const marginUnlocked = !!activeInvestments['inv-tech-01']
    && (!!hiredSupport['role-lea-bertrand']
      || !!hiredSupport['role-marc-dupuis']
      || !!hiredSupport['role-sylvie-marchand']);
  const margin = (marginUnlocked && selectedVehicle && selectedDriver)
    ? computeContractMargin(contract, selectedVehicle, selectedDriver)
    : null;

  // 04/06/2026 — Vehicle/driver conflict guard. When the slice returns
  // `assignment_conflict`, we surface a confirmation modal listing the
  // contracts that would be displaced. The user can cancel (= keep their
  // current contracts) or force the reassignment.
  const [pendingConflict, setPendingConflict] = useState(null);

  const doAccept = (force = false) => {
    const priceMultiplier = contract._priceMultiplier;
    const argumentationOutcome = contract._argumentationOutcome;
    const opts = (priceMultiplier || argumentationOutcome || force) ? { priceMultiplier, argumentationOutcome, force } : undefined;
    const res = acceptContract(contract.instanceId, selectedVehicle.id, selectedDriver.id, opts);
    if (res && res.ok === false && res.reason === 'assignment_conflict') {
      setPendingConflict(res);
      return false;
    }
    // 24/07/2026 — contrats locaux : chauffeur local du pays obligatoire.
    if (res && res.ok === false && res.reason === 'requires_local_driver') {
      toast('Chauffeur local requis', /* i18n-keep */ {
        description: `Ce contrat de cabotage exige un chauffeur local (${EXPORT_COUNTRIES[res.country]?.name || res.country}). Recrutez-en un via RH → Recrutement → Recrutement local (Export).`, /* i18n-keep */
      });
      return false;
    }
    // 17/06/2026 (Lot 4 v2) — Rémi Forni transparent : le contrat est
    // accepté en différé pendant que le camion est transféré au bon dépôt.
    // Affiche un toast clair et ferme la modale ; le contrat s'activera
    // automatiquement à l'arrivée (cf. promotePendingTransferContracts).
    if (res && res.ok === true && res.deferred && res.reason === 'remi_auto_transfer') {
      toast('Rémi Forni gère le transfert inter-dépôts', /* i18n-keep */ {
        description: `Un camion compatible est en route vers le bon dépôt (convoyage ${formatMoney(res.cost || 0)} · arrivée J+1). Le contrat sera signé automatiquement dès l'arrivée — vous n'avez rien à faire.`, /* i18n-keep */
      });
      return true;
    }
    return true;
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    playSound('contractSign');
    // 03/06/2026 Phase 3 bis — pass argumentation priceMultiplier
    // (carried on the contract object as `_priceMultiplier`).
    const ok = doAccept(false);
    if (ok) onClose();
  };

  const handleForceConfirm = () => {
    setPendingConflict(null);
    doAccept(true);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="contract-assign-overlay"
    >
      <div
        className={`bg-ink-800 rounded-xl w-full ${contract?.international ? 'max-w-5xl' : 'max-w-3xl'} shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300 flex flex-col max-h-[90vh]`}
        data-testid="contract-assign-modal"
      >
        {/* Header */}
        <div className="bg-accent px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-blue-200 text-xs font-medium uppercase tracking-wider">{t('contract_assignment') || 'Assignation contrat'}</p>
            <h3 className="text-foreground font-bold text-lg">{contract.name}</h3>
          </div>
          <button aria-label="Fermer" onClick={onClose} className="text-foreground/60 hover:text-foreground transition-colors" data-testid="close-assign-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Contract summary + requirements */}
        <ContractSummaryBar contract={contract} />

        {/* 13/06/2026 — Chantier Dépôts : avertissement hors zone */}
        <OutOfZoneBanner contract={contract} />

        {/* Phase 3 : camions du mauvais dépôt masqués */}
        {wrongDepotCount > 0 && (
          <div
            className="mx-5 mt-3 bg-sky-500/10 border border-sky-500/40 rounded-lg px-3 py-2 flex gap-2 items-start"
            data-testid="wrong-depot-notice"
          >
            <AlertCircle className="w-4 h-4 text-sky-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-sky-100 leading-snug">
              {t('assign_wrong_depot_notice', {
                count: wrongDepotCount,
                city: findCityById(contract.originCityId)?.name || '?',
              })}
            </p>
          </div>
        )}

        {/* 05/02/2026 (Q1 Lot 1) — Panneau transfert inter-dépôts pour
            staffer un AO. Visible uniquement quand des véhicules libres sont
            dans un mauvais dépôt ET qu'un dépôt cible couvre le contrat. */}
        {wrongDepotVehicles.length > 0 && targetDepotForTransfer && (
          <WrongDepotTransferPanel
            wrongDepotVehicles={wrongDepotVehicles}
            targetDepot={targetDepotForTransfer}
            company={company}
            originCityName={findCityById(contract.originCityId)?.name || '—'}
          />
        )}

        {/* International trade pedagogical panel — visible only for intra/extra-EU contracts */}
        <InternationalTradePanel contract={contract} />

        {/* Selection columns */}
        <div className="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-0 divide-x divide-ink-600">
          <VehicleColumn
            vehiclesWithCompat={vehiclesWithCompat}
            selectedVehicleId={selectedVehicleId}
            onSelect={setSelectedVehicleId}
          />
          <DriverColumn
            driversWithCompat={driversWithCompat}
            selectedDriverId={selectedDriverId}
            contract={contract}
            onSelect={setSelectedDriverId}
          />
        </div>

        {/* Margin block (TMS + commercial unlock) */}
        {margin && <MarginBlock margin={margin} />}

        {/* Permit cross-check warning */}
        {selectedVehicle && selectedDriver && !permitCompat.ok && (
          <div
            className="px-4 py-2 border-t border-warn/40 bg-warn/10 flex items-center gap-2 text-xs text-warn"
            data-testid="permit-missing-warning"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>
              <MarkdownLite text={t('contract_permit_missing_warning', {
                name: selectedDriver.name,
                required: permitCompat.required,
                owned: permitCompat.owned.join(', ') || '—',
              })} />
            </span>
          </div>
        )}

        {/* HSE compliance soft-block warning */}
        {selectedVehicle && selectedDriver && hseBlock.blocked && (
          <div
            className="px-4 py-2 border-t border-danger/40 bg-danger/10 flex items-center gap-2 text-xs text-danger"
            data-testid="hse-block-warning"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>
              <MarkdownLite text={t(
                hseBlock.reason === 'medical_expired'
                  ? 'contract_hse_medical_blocked'
                  : 'contract_hse_adr_blocked',
                { name: selectedDriver.name, expiresOn: hseBlock.expiresOn },
              )} />
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-3 border-t border-ink-600 bg-ink-700/30 flex items-center justify-between flex-wrap gap-2">
          <div className="text-[11px] text-slate-400 flex items-center gap-2">
            {canConfirm && specialtyBonus && (
              <span className="flex items-center gap-1 text-money font-mono uppercase tracking-wider" data-testid="perfect-match-badge">
                <BadgeCheck className="w-3.5 h-3.5" /> {t('perfect_match')}
              </span>
            )}
            {!canConfirm && (selectedVehicle || selectedDriver) && (
              <span className="text-warn">{t('incompatible_short')}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="text-xs text-slate-400 hover:text-slate-200 px-3 py-2 transition-colors"
              data-testid="cancel-assign-btn"
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={!canConfirm}
              className="bg-emerald-600 text-white px-5 py-2.5 rounded-lg text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-emerald-700 transition-colors flex items-center gap-2"
              data-testid="confirm-assign-btn"
            >
              <Check className="w-4 h-4" />
              {t('confirm')}
            </button>
          </div>
        </div>
      </div>

      <AssignmentConflictModal
        conflict={pendingConflict}
        onCancel={() => setPendingConflict(null)}
        onForce={handleForceConfirm}
      />
    </div>
  );
};

/* ── Sub-components (kept in this file to preserve i18nDrift expectations) ── */

function ContractSummaryBar({ contract }) {
  const { t } = useTranslation();
  const vatCode = getVatRegime(contract);
  const vat = VAT_REGIMES[vatCode];
  return (
    <div className="px-5 py-3 border-b border-ink-600 bg-ink-700/30 space-y-2">
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="px-2 py-1 rounded bg-accent/10 text-accent font-medium">{contract.category}</span>
        <span
          className="px-2 py-1 rounded bg-money/10 text-money font-medium"
          title={`${vat.label} — ${vat.mention}`}
          data-testid={`vat-badge-${vatCode}`}
        >
          {contract.frequency === 'ponctuel' ? formatMoney(contract.revenue) : `${formatMoney(contract.dailyRevenue || contract.revenue)}/jour`}
          <span className="ml-1 text-[10px] text-money/70 font-mono">
            HT — {vat.short}
          </span>
        </span>
        <span className="px-2 py-1 rounded bg-warn/10 text-warn font-medium">
          {contract.frequency === 'ponctuel' ? `${contract.duration || 1} jour(s)` : `${contract.duration || 30} jours`}
        </span>
      </div>
      {/* Requirements badges */}
      {/* 05/02/2026 — Q1 retour user prod : lisibilité light theme améliorée
          (dual-mode `dark:text-X-300 text-X-700`). */}
      <div className="flex flex-wrap gap-2 text-[10px]">
        <span className="px-2 py-1 rounded bg-ink-700 text-slate-300 border border-ink-600 font-mono">
          {t('requires_tier', { tier: contract.tierMin })}+
        </span>
        {contract.requiresLicense && (
          <span className="px-2 py-1 rounded bg-danger/15 text-danger border border-danger/30 font-mono" data-testid="req-license">
            {t('requires_license', { license: contract.requiresLicense })}
          </span>
        )}
        {CATEGORY_VEHICLE_REQUIREMENTS[contract.category] && (
          <span className="px-2 py-1 rounded bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border border-cyan-500/40 dark:border-cyan-500/30 font-mono" data-testid="req-equipment">
            {t('contract_equipment_label')} : {contract.category}
          </span>
        )}
        {contract.requiresSpecialty && (
          <span className="px-2 py-1 rounded bg-amber-500/15 text-amber-800 dark:text-amber-400 border border-amber-500/50 dark:border-amber-500/30 font-mono" data-testid="req-specialty">
            {t('requires_specialty', { specialty: contract.requiresSpecialty })}
          </span>
        )}
      </div>
    </div>
  );
}

function VehicleColumn({ vehiclesWithCompat, selectedVehicleId, onSelect }) {
  const { t } = useTranslation();
  return (
    <div className="p-4">
      <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2 flex items-center gap-2">
        <Truck className="w-3.5 h-3.5" /> {t('select_vehicle')}
      </h4>
      {vehiclesWithCompat.length === 0 ? (
        <p className="text-xs text-slate-500 italic py-4 text-center">{t('no_vehicle_meets_criteria')}</p>
      ) : (
        <div className="space-y-1.5">
          {vehiclesWithCompat.map(({ v, compat }) => {
            const isSelected = selectedVehicleId === v.id;
            const blocked = !compat.ok;
            return (
              <button
                key={v.id}
                onClick={() => !blocked && onSelect(v.id)}
                disabled={blocked}
                className={`w-full text-left border-2 rounded-lg p-2.5 transition-all ${
                  blocked
                    ? 'border-ink-600 bg-ink-700/20 opacity-50 cursor-not-allowed'
                    : isSelected
                      ? 'border-accent bg-accent/10 shadow-sm'
                      : 'border-ink-600 hover:border-ink-500 hover:bg-ink-700/40'
                }`}
                data-testid={`assign-vehicle-${v.id}`}
              >
                <div className="flex items-start gap-2">
                  <div className={`p-1.5 rounded ${isSelected ? 'bg-accent text-white' : 'bg-ink-700/60 text-slate-400'}`}>
                    <Truck className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] px-1 py-0.5 rounded bg-accent text-white font-bold">T{v.tier}</span>
                      <h5 className="font-semibold text-slate-100 text-xs truncate">{v.brand} {v.model}</h5>
                      {v.isLeased && <Lock className="w-3 h-3 text-purple-400" title="Leasing" />}
                    </div>
                    <div className="flex gap-2 mt-0.5 text-[10px] text-slate-500">
                      <span className="flex items-center gap-0.5"><Fuel className="w-2.5 h-2.5" />{formatMoney(v.dailyFuel)}/j</span>
                      <span className="flex items-center gap-0.5"><Shield className="w-2.5 h-2.5" />{formatMoney(v.insurancePerMonth)}/m</span>
                    </div>
                    {blocked && (
                      <p className="text-[10px] text-danger mt-1 flex items-center gap-1">
                        <AlertCircle className="w-2.5 h-2.5" />
                        {compat.reason === 'tier' && t('requires_tier', { tier: compat.value })}
                        {compat.reason === 'equipment' && t('contract_equipment_required', { value: compat.value })}
                        {compat.reason === 'specialty' && t('requires_specialty', { specialty: compat.value })}
                      </p>
                    )}
                  </div>
                  {isSelected && <Check className="w-4 h-4 text-accent flex-shrink-0" />}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DriverColumn({ driversWithCompat, selectedDriverId, contract, onSelect }) {
  const { t } = useTranslation();
  return (
    <div className="p-4">
      <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2 flex items-center gap-2">
        <User className="w-3.5 h-3.5" /> {t('select_driver')}
      </h4>
      {driversWithCompat.length === 0 ? (
        <p className="text-xs text-slate-500 italic py-4 text-center">{t('no_driver_available_label')}</p>
      ) : (
        <div className="space-y-1.5">
          {driversWithCompat.map(({ d, compat, bonus }) => {
            const isSelected = selectedDriverId === d.id;
            const blocked = !compat.ok;
            return (
              <button
                key={d.id}
                onClick={() => !blocked && onSelect(d.id)}
                disabled={blocked}
                className={`w-full text-left border-2 rounded-lg p-2.5 transition-all ${
                  blocked
                    ? 'border-ink-600 bg-ink-700/20 opacity-50 cursor-not-allowed'
                    : isSelected
                      ? 'border-money bg-money/10 shadow-sm'
                      : 'border-ink-600 hover:border-ink-500 hover:bg-ink-700/40'
                }`}
                data-testid={`assign-driver-${d.id}`}
              >
                <div className="flex items-start gap-2">
                  <div className={`p-1.5 rounded ${isSelected ? 'bg-money text-white' : 'bg-ink-700/60 text-slate-400'}`}>
                    <User className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <h5 className="font-semibold text-slate-100 text-xs truncate">{d.name}</h5>
                      <span className="text-[10px] px-1 rounded bg-ink-700 text-slate-400">{t('contract_driver_level', { level: d.level })}</span>
                      {bonus && !blocked && (
                        <span className="flex items-center gap-0.5 px-1 py-0.5 rounded bg-money/20 text-money" data-testid={`driver-bonus-${d.id}`}>
                          <Zap className="w-2.5 h-2.5" />
                          <span className="text-[10px] font-bold">+15%</span>
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {getDriverSpecialties(d).join(' · ') || '—'}
                    </p>
                    <div className="flex gap-1 mt-1" data-testid={`driver-licenses-${d.id}`}>
                      {(d.licenses || ['B']).map(lic => {
                        const meetsReq = contract.requiresLicense && lic === contract.requiresLicense;
                        return (
                          <span
                            key={lic}
                            className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                              meetsReq
                                ? 'bg-money/20 text-money border border-money/40'
                                : 'bg-accent/10 text-accent border border-accent/30'
                            }`}
                            data-testid={`driver-license-${d.id}-${lic}`}
                          >
                            {lic}
                          </span>
                        );
                      })}
                    </div>
                    <div className="flex gap-1.5 mt-1.5">
                      <div className="w-14">
                        <Gauge value={d.fatigue} type="fatigue" showValue={false} />
                      </div>
                      <div className="w-14">
                        <Gauge value={d.satisfaction} type="satisfaction" showValue={false} />
                      </div>
                    </div>
                    {blocked && (
                      <p className="text-[10px] text-danger mt-1 flex items-center gap-1">
                        <AlertCircle className="w-2.5 h-2.5" />
                        {compat.reason === 'license' && t('requires_license', { license: compat.value })}
                        {compat.reason === 'driver_specialty' && t('contract_driver_specialty_required', { value: compat.value })}
                        {compat.reason === 'local_driver' && `Chauffeur local requis (${EXPORT_COUNTRIES[compat.value]?.name || compat.value})`}
                      </p>
                    )}
                  </div>
                  {isSelected && <Check className="w-4 h-4 text-money flex-shrink-0" />}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MarginBlock({ margin }) {
  const { t } = useTranslation();
  return (
    <div className={`px-4 py-2 border-t border-ink-600 ${margin.margin < 0 ? 'bg-danger/5' : 'bg-money/5'}`} data-testid="margin-block-modal">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {margin.margin < 0
            ? <TrendingDown className="w-4 h-4 text-danger" />
            : <TrendingUp className="w-4 h-4 text-money" />}
          <p className="text-[10px] uppercase tracking-wider font-mono font-bold text-slate-300">
            {t('contract_estimated_gross_margin')}
          </p>
        </div>
        <div className="text-right">
          <p className={`text-lg font-black font-mono ${margin.margin < 0 ? 'text-danger' : 'text-money'}`} data-testid="margin-modal-value">
            {formatMoney(margin.margin)} <span className="text-[11px] opacity-80">({margin.marginPct}%)</span>
          </p>
          {margin.margin < 0 && (
            <p className="text-[10px] text-danger font-bold">{t('contract_avoid_deficit')}</p>
          )}
          {margin.margin >= 0 && margin.marginPct > MARKET_EQUILIBRIUM_MAX_GROSS_MARGIN * 100 && (
            <p className="text-[10px] text-orange-300 font-bold flex items-center gap-1 justify-end" data-testid="margin-market-cap-hint">
              <AlertTriangle className="w-3 h-3" />
              {t('contract_margin_market_cap_hint', { cap: Math.round(MARKET_EQUILIBRIUM_MAX_GROSS_MARGIN * 100) })}
            </p>
          )}
        </div>
      </div>
      <div className="mt-1.5 grid grid-cols-6 gap-1 text-[10px] text-slate-400 font-mono">
        <div className="text-money">+ {formatMoney(margin.revenue)}</div>
        <div className="text-danger">− {formatMoney(margin.details.fuel)} {t('contract_fuel_short')}</div>
        <div className="text-danger">− {formatMoney(margin.details.maintenance)} {t('contract_maintenance_short')}</div>
        <div className="text-danger">− {formatMoney(margin.details.driver)} {t('contract_salary_short')}</div>
        <div className="text-danger">− {formatMoney(margin.details.insurance)} {t('contract_insurance_short')}</div>
        <div className="text-danger">− {formatMoney(margin.details.toll || 0)} {t('contract_toll_short')}</div>
      </div>
    </div>
  );
}

/**
 * 05/02/2026 (Q1 Lot 1) — Panneau transfert inter-dépôts dans le flux
 * d'assignation d'un AO. Permet au joueur de transférer un véhicule libre
 * situé dans un dépôt qui NE COUVRE PAS le contrat vers le dépôt cible (le
 * premier dépôt couvrant la ville d'origine).
 *
 * Le transfert prend 1 jour (arrivée J+1), débite le coût de convoyage,
 * et le véhicule devient assignable à l'arrivée. L'utilisateur doit donc
 * fermer la modale et la rouvrir le lendemain (UX cohérente avec le
 * mécanisme Rémi Forni existant).
 */
function WrongDepotTransferPanel({ wrongDepotVehicles, targetDepot, company, originCityName }) {
  const transferVehicle = useGameStore(s => s.transferVehicle);
  const treasury = useGameStore(s => s.treasury) || 0;

  const onTransfer = (vehicleId, fromDepot) => {
    const cost = getVehicleTransferCost(fromDepot, targetDepot, company);
    if (treasury < cost) {
      toast.error(`Trésorerie insuffisante pour transfert (${cost.toLocaleString('fr-FR')} €).`); // i18n-keep — toast FR
      return;
    }
    const res = transferVehicle(vehicleId, targetDepot.id);
    if (!res || !res.ok) {
      const reason = res?.reason || 'unknown';
      const msg = reason === 'target_depot_full'
        ? `Dépôt cible saturé : ${res.occupancy ?? '?'}/${res.capacity} places occupées par des camions stationnés (les camions en mission ne comptent pas). Libérez une place ou agrandissez le parking (« Extension parking » +5 places).` // i18n-keep
        : reason === 'insufficient_funds'
          ? `Trésorerie insuffisante (convoyage ${(res.cost || 0).toLocaleString('fr-FR')} €).` // i18n-keep
          : reason === 'target_depot_not_operational'
            ? 'Dépôt cible sans responsable de site recruté.' // i18n-keep
            : 'Transfert impossible.'; /* i18n-keep */
      toast.error(msg);
      return;
    }
    const targetCity = findCityById(targetDepot.cityId)?.name || targetDepot.name;
    toast.success(`Transfert lancé vers ${targetCity} (arrivée demain). Re-ouvrez l'AO dès J+1 pour staffer.`); // i18n-keep — toast FR
  };

  const targetCityName = findCityById(targetDepot.cityId)?.name || targetDepot.name;

  return (
    <div
      className="mx-5 mt-3 bg-purple-500/10 border border-purple-500/40 rounded-lg px-3 py-2.5"
      data-testid="wrong-depot-transfer-panel"
    >
      <div className="flex items-center gap-2 mb-2">
        <Truck className="w-4 h-4 text-purple-300" />
        <p className="text-[11px] uppercase tracking-widest text-purple-200 font-bold font-mono">
          Transférer un véhicule pour couvrir {originCityName/* i18n-keep — city name */}
        </p>
      </div>
      <p className="text-[10px] text-slate-400 mb-2">
        Cible : <strong className="text-purple-200">{targetCityName/* i18n-keep — city name */}</strong>
        {' — '}arrivée J+1, 1 jour d'immobilisation.
      </p>
      <div className="space-y-1">
        {wrongDepotVehicles.slice(0, 6).map(({ vehicle, depot }) => {
          const cost = getVehicleTransferCost(depot, targetDepot, company);
          const fromCityName = findCityById(depot.cityId)?.name || depot.name;
          return (
            <div
              key={vehicle.id}
              className="flex items-center justify-between gap-2 bg-ink-800/40 rounded px-2 py-1.5"
              data-testid={`wrong-depot-vehicle-${vehicle.id}`}
            >
              <div className="text-xs text-slate-200">
                <span className="font-semibold">{vehicle.brand}{' '}{vehicle.model}</span>
                <span className="text-[10px] text-slate-500 ml-1">
                  📍 {fromCityName/* i18n-keep */}
                </span>
              </div>
              <button
                type="button"
                onClick={() => onTransfer(vehicle.id, depot)}
                disabled={treasury < cost}
                className="text-[11px] px-2 py-1 rounded bg-purple-600/20 text-purple-200 border border-purple-500/50 hover:bg-purple-600/30 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5 font-mono"
                data-testid={`transfer-for-contract-${vehicle.id}`}
              >
                ⇄ Transférer (−{cost.toLocaleString('fr-FR')} €){/* i18n-keep */}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ContractAssignModal;
